const isMockFlowEnabled =
  (process.env.REACT_APP_PAYMENT_FLOW_MODE || 'mock') !== 'live';

// Online payments (card, wallets) are off when the build says so, and always in a production build
// that is still in mock mode: a production page must never simulate a payment. Cash and bank transfer
// stay. Set REACT_APP_PAYMENT_FLOW_MODE=live (and the Razorpay key) to turn card payments on, and
// REACT_APP_ONLINE_PAYMENTS=off to keep them hidden until the live key is ready.
export const onlinePaymentsOff =
  process.env.REACT_APP_ONLINE_PAYMENTS === 'off' ||
  (process.env.NODE_ENV === 'production' && isMockFlowEnabled);
const OFFLINE_PROVIDERS = ['cash', 'bank_transfer'];
const keepAllowedMethods = (methods) =>
  onlinePaymentsOff
    ? methods.filter(
        (m) => OFFLINE_PROVIDERS.includes(m.provider) || ['cash', 'bank'].includes(m.methodType)
      )
    : methods;

console.log('🔍 PAYMENT FLOW DEBUG:', {
  REACT_APP_PAYMENT_FLOW_MODE: process.env.REACT_APP_PAYMENT_FLOW_MODE,
  isMockFlowEnabled,
  onlinePaymentsOff,
  paymentFlowMode: isMockFlowEnabled ? 'mock' : 'live'
});

const shouldForceShowWallets =
  process.env.NODE_ENV !== 'production' &&
  (process.env.REACT_APP_FORCE_SHOW_ALL_WALLETS ?? 'true') === 'true';

const DEFAULT_METHODS = [
  {
    id: 'apple_pay',
    methodName: 'Apple Pay',
    methodType: 'wallet',
    provider: 'apple_pay',
  },
  {
    id: 'google_pay',
    methodName: 'Google Pay',
    methodType: 'wallet',
    provider: 'google_pay',
  },
  {
    id: 'card',
    methodName: 'Debit/Credit Card',
    methodType: 'card',
    provider: 'stripe',
  },
  {
    id: 'cash',
    methodName: 'Cash',
    methodType: 'cash',
    provider: 'cash',
  },
];

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const getUniqueMethods = (methods) =>
  methods.filter(
    (method, index, list) =>
      index ===
      list.findIndex(
        (candidate) =>
          candidate.provider === method.provider ||
          normalizeText(candidate.methodName) === normalizeText(method.methodName)
      )
  );

export const detectWalletSupport = () => {
  if (typeof window === 'undefined') {
    return {
      applePay: false,
      googlePay: false,
    };
  }

  const userAgent = window.navigator.userAgent || '';
  const vendor = window.navigator.vendor || '';

  const isAppleDevice = /iPhone|iPad|iPod|Macintosh/i.test(userAgent);
  const isSafari =
    /Safari/i.test(userAgent) &&
    !/Chrome|Chromium|CriOS|Edg|OPR/i.test(userAgent) &&
    /Apple/i.test(vendor);
  const isGoogleEnvironment =
    /Android/i.test(userAgent) ||
    /Chrome|Chromium|CriOS/i.test(userAgent);

  return {
    applePay: isAppleDevice && isSafari,
    googlePay: isGoogleEnvironment,
  };
};

export const normalizePaymentMethod = (paymentOption) => {
  const methodName = String(paymentOption?.methodName || paymentOption?.name || 'Payment');
  const normalizedName = normalizeText(methodName);


  if (normalizedName.includes('apple')) {
    return {
      id: paymentOption?.id ?? 'apple_pay',
      methodName: 'Apple Pay',
      methodType: 'wallet',
      provider: 'apple_pay',
    };
  }

  if (
    normalizedName.includes('google') ||
    normalizedName.includes('g pay') ||
    normalizedName.includes('gpay')
  ) {
    return {
      id: paymentOption?.id ?? 'google_pay',
      methodName: 'Google Pay',
      methodType: 'wallet',
      provider: 'google_pay',
    };
  }

  if (normalizedName.includes('razorpay')) {
    return {
      id: paymentOption?.id ?? 'razorpay_card',
      methodName: 'Razorpay Card/Wallet',
      methodType: 'card',
      provider: 'razorpay',
    };
  }

  if (normalizedName.includes('upi') || normalizedName.includes('wallet')) {
    return {
      id: paymentOption?.id ?? 'google_pay',
      methodName: methodName,
      methodType: 'wallet',
      provider: 'google_pay',
    };
  }

  if (normalizedName.includes('cash')) {
    return {
      id: paymentOption?.id ?? 'cash',
      methodName: 'Cash',
      methodType: 'cash',
      provider: 'cash',
    };
  }

  if (normalizedName.includes('bank')) {
    return {
      id: paymentOption?.id ?? 'bank_transfer',
      methodName: methodName,
      methodType: 'bank',
      provider: 'bank_transfer',
    };
  }

  return {
    id: paymentOption?.id ?? 'card',
    methodName: 'Debit/Credit Card',
    methodType: 'card',
    provider: 'stripe',
  };
};

export const resolveAvailablePaymentMethods = (backendMethods = []) => {
  const supportedWallets = detectWalletSupport();
  const normalizedBackendMethods = backendMethods.map(normalizePaymentMethod);

  if (!isMockFlowEnabled && normalizedBackendMethods.length > 0) {
    return keepAllowedMethods(getUniqueMethods(normalizedBackendMethods));
  }

  const simulatedMethods = DEFAULT_METHODS.filter((method) => {
    if (shouldForceShowWallets) {
      return true;
    }

    if (method.provider === 'apple_pay') {
      return supportedWallets.applePay;
    }

    if (method.provider === 'google_pay') {
      return supportedWallets.googlePay;
    }

    return true;
  });

  return keepAllowedMethods(getUniqueMethods([...normalizedBackendMethods, ...simulatedMethods]));
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const createTransactionId = (prefix) =>
  `${prefix}-${Math.random().toString(36).slice(2, 8).toUpperCase()}${Date.now()
    .toString()
    .slice(-4)}`;

export const simulatePaymentAuthorization = async ({
  provider,
  amountFormatted,
  cardNumber,
}) => {
  await wait(provider === 'cash' ? 600 : 1400);

  if (provider === 'card' || provider === 'razorpay') {
    const sanitizedCard = String(cardNumber || '').replace(/\D/g, '');

    if (sanitizedCard.endsWith('0002')) {
      return {
        ok: false,
        status: 'failed',
        message: 'This test card was declined. Try another card number.',
      };
    }

    const providerLabel = provider === 'razorpay' ? 'Razorpay' : 'Card';
    return {
      ok: true,
      status: 'paid',
      statusLabel: `${providerLabel} payment authorized`,
      transactionId: createTransactionId(provider === 'razorpay' ? 'RZP' : 'CARD'),
      receiptLabel: `Card ending in ${sanitizedCard.slice(-4) || '4242'}`,
      message: `Your payment of ${amountFormatted} was authorized successfully.`,
    };
  }

  if (provider === 'apple_pay') {
    return {
      ok: true,
      status: 'paid',
      statusLabel: 'Apple Pay authorized',
      transactionId: createTransactionId('APL'),
      receiptLabel: 'Apple Pay',
      message: `Apple Pay authorized the payment of ${amountFormatted}.`,
    };
  }

  if (provider === 'google_pay') {
    return {
      ok: true,
      status: 'paid',
      statusLabel: 'Google Pay authorized',
      transactionId: createTransactionId('GGL'),
      receiptLabel: 'Google Pay',
      message: `Google Pay authorized the payment of ${amountFormatted}.`,
    };
  }

  if (provider === 'cash') {
    return {
      ok: true,
      status: 'pending',
      statusLabel: 'Awaiting cash collection',
      transactionId: createTransactionId('CASH'),
      receiptLabel: 'Cash payment',
      message: `Cash payment for ${amountFormatted} has been recorded for offline collection.`,
    };
  }

  if (provider === 'bank_transfer') {
    return {
      ok: true,
      status: 'pending',
      statusLabel: 'Awaiting bank transfer',
      transactionId: createTransactionId('BANK'),
      receiptLabel: 'Bank transfer',
      message: `Bank transfer request for ${amountFormatted} has been created.`,
    };
  }

  return {
    ok: true,
    status: 'paid',
    statusLabel: 'Payment authorized',
    transactionId: createTransactionId('PAY'),
    receiptLabel: 'Payment',
    message: `Payment for ${amountFormatted} has been authorized.`,
  };
};

export const paymentFlowMode = isMockFlowEnabled ? 'mock' : 'live';

export const tokenizeCardWithStripe = async (stripe, cardNumber, expiry, cvc) => {
  if (!stripe) {
    throw new Error('Stripe not initialized');
  }

  const [month, year] = expiry.split('/');
  const fullYear = year ? `20${year}` : '';

  const { paymentMethod, error } = await stripe.createPaymentMethod({
    type: 'card',
    card: {
      number: cardNumber.replace(/\s/g, ''),
      exp_month: month,
      exp_year: fullYear,
      cvc: cvc,
    },
  });

  if (error) {
    throw error;
  }

  const lastFour = cardNumber.replace(/\s/g, '').slice(-4);

  return {
    ok: true,
    status: 'paid',
    statusLabel: 'Stripe payment authorized',
    transactionId: paymentMethod.id,
    receiptLabel: `Card ending in ${lastFour}`,
    message: `Stripe payment for card ending in ${lastFour} authorized.`,
    token: paymentMethod.id,
  };
};

export const createPaymentRequest = (stripe, amount, currency, paymentMethodType) => {
  if (!stripe) {
    throw new Error('Stripe not initialized');
  }

  const paymentRequest = stripe.paymentRequest({
    country: 'US',
    currency: currency.toLowerCase(),
    total: {
      label: `eKutir Subscription`,
      amount: Math.round(amount * 100), // Amount in cents
    },
    requestPayerEmail: true,
    requestPayerName: true,
    requestPayerPhone: true,
  });

  return paymentRequest;
};

export const confirmWalletPayment = async (stripe, clientSecret, paymentMethodType) => {
  if (!stripe) {
    console.error('Stripe instance is not available');
    throw new Error('Stripe not initialized');
  }

  if (!clientSecret) {
    console.error('Client secret is missing');
    throw new Error('Client secret is required');
  }

  try {
    console.log(`=== Confirming ${paymentMethodType} Payment ===`);
    console.log(`Payment Method Type: ${paymentMethodType}`);
    console.log(`ClientSecret: ${clientSecret.substring(0, 20)}...`);

    // confirmPayment handles all payment method types including Apple Pay and Google Pay
    // The redirect: 'if_required' option means it will only redirect if payment needs further action
    const result = await stripe.confirmPayment({
      clientSecret: clientSecret,
      redirect: 'if_required',
    });

    console.log('confirmPayment result:', {
      hasPaymentIntent: !!result.paymentIntent,
      hasError: !!result.error,
      errorMessage: result.error?.message,
      paymentIntentStatus: result.paymentIntent?.status
    });

    if (result.error) {
      console.error(`${paymentMethodType} payment error:`, result.error);
      return {
        ok: false,
        status: 'failed',
        message: result.error.message || `${paymentMethodType} payment failed. Please try again.`,
      };
    }

    if (!result.paymentIntent) {
      console.error('No paymentIntent returned from confirmPayment');
      return {
        ok: false,
        status: 'failed',
        message: 'Payment could not be processed. Please try again.',
      };
    }

    const { paymentIntent } = result;

    console.log(`PaymentIntent status: ${paymentIntent.status}`);

    if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing') {
      console.log(`✅ ${paymentMethodType} payment authorized. Status: ${paymentIntent.status}`);
      return {
        ok: true,
        status: paymentIntent.status,
        message: `${paymentMethodType} payment confirmed`,
        transactionId: paymentIntent.id,
      };
    } else {
      console.warn(`⚠️ Unexpected PaymentIntent status: ${paymentIntent.status}`);
      return {
        ok: false,
        status: 'failed',
        message: `Payment status is "${paymentIntent.status}". Please try again.`,
      };
    }
  } catch (error) {
    console.error(`❌ Exception in ${paymentMethodType} payment confirmation:`, error);
    return {
      ok: false,
      status: 'failed',
      message: error.message || `An error occurred during ${paymentMethodType} payment.`,
    };
  }
};

export const handle3DSChallenge = async (stripe, clientSecret) => {
  if (!stripe) {
    console.error('Stripe instance is not available');
    throw new Error('Stripe not initialized');
  }

  try {
    console.log('=== 3DS Challenge Started ===');
    console.log('Stripe instance:', typeof stripe);
    console.log('ClientSecret provided:', !!clientSecret);
    console.log('ClientSecret:', clientSecret ? clientSecret.substring(0, 20) + '...' : 'MISSING');

    // Use confirmCardPayment to handle 3D Secure authentication
    // The PaymentIntent is already created on the backend with a payment method attached
    // confirmCardPayment will handle the 3DS challenge and return the result
    console.log('Calling stripe.confirmCardPayment()...');
    const result = await stripe.confirmCardPayment(clientSecret);
    console.log('stripe.confirmCardPayment() returned:', {
      hasPaymentIntent: !!result.paymentIntent,
      hasError: !!result.error,
      errorMessage: result.error?.message,
      paymentIntentStatus: result.paymentIntent?.status
    });

    const { paymentIntent, error } = result;

    if (error) {
      console.error('3DS challenge error from Stripe:', error);
      return {
        ok: false,
        status: 'failed',
        message: error.message || '3D Secure authentication failed. Please try again.',
      };
    }

    if (!paymentIntent) {
      console.error('No paymentIntent returned from confirmCardPayment');
      return {
        ok: false,
        status: 'failed',
        message: 'Payment intent could not be processed. Please try again.',
      };
    }

    console.log('PaymentIntent received:', {
      id: paymentIntent.id,
      status: paymentIntent.status,
      clientSecret: paymentIntent.client_secret ? 'present' : 'missing'
    });

    // After confirmCardPayment completes, the PaymentIntent should be in one of these states:
    // - 'succeeded' - User authenticated and payment succeeded
    // - 'processing' - Payment is processing
    // - 'requires_action' - Additional action still required (shouldn't happen if 3DS completed)

    if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing') {
      console.log('✅ 3DS authentication successful. PaymentIntent status:', paymentIntent.status);
      return {
        ok: true,
        status: paymentIntent.status,
        message: '3D Secure authentication successful',
        clientSecret: clientSecret,
      };
    } else if (paymentIntent.status === 'requires_action') {
      console.warn('⚠️ PaymentIntent still requires action after confirmCardPayment');
      return {
        ok: false,
        status: 'requires_action',
        message: 'Payment authentication incomplete. Please try again.',
        clientSecret: clientSecret,
      };
    } else {
      console.warn('⚠️ Unexpected PaymentIntent status after 3DS:', paymentIntent.status);
      return {
        ok: false,
        status: 'failed',
        message: `Unexpected payment status: ${paymentIntent.status}. Please try again.`,
      };
    }
  } catch (error) {
    console.error('❌ Exception in 3DS challenge handling:', error);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      type: error.type,
      stack: error.stack
    });
    return {
      ok: false,
      status: 'failed',
      message: error.message || 'An error occurred during 3D Secure authentication.',
    };
  }
};
